import * as React from 'react';
import type { ITextField } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { usePatchPartialPayload, useTextChangeCallback } from 'owa-timestream-asset-sdk';
import type { BucketPayload } from 'owa-timestream-asset-sdk-bucket';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';

export default function useBucketViewTitleState(bucketName: string) {
    const patchPartialPayload = usePatchPartialPayload<BucketPayload>();
    const bucketNameFieldRef = React.useRef<ITextField>(null);
    const [newBucketName, setBucketName] = React.useState(bucketName);

    // if there is a pre-existing bucket name, update when that info is given
    React.useEffect(() => {
        setBucketName(bucketName!);
    }, [bucketName]);

    const onChange = useTextChangeCallback(setBucketName);

    const onTextFieldBlur = React.useCallback(() => {
        // trimming to handle the case where user enters only spaces the very first time
        const trimmedName = newBucketName.trim();
        if (!!trimmedName) {
            if (trimmedName !== bucketName) {
                patchPartialPayload({
                    name: trimmedName,
                });
            }
        } else {
            // return the name to the old name if the new name is empty
            setBucketName(bucketName);
        }
    }, [newBucketName, bucketName]);

    const onBlur = useOnBlurUpdate(onTextFieldBlur);

    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            switch (ev.keyCode) {
                case KeyCodes.enter:
                    !!bucketNameFieldRef &&
                        !!bucketNameFieldRef.current &&
                        bucketNameFieldRef.current.blur();
                    break;
                case KeyCodes.escape:
                    setBucketName(bucketName);
                    break;
            }
        },
        [bucketNameFieldRef, bucketName]
    );

    return {
        componentRef: bucketNameFieldRef,
        onBlur,
        onChange,
        onKeyDown,
        value: newBucketName,
    };
}
